import { defineStore } from 'pinia'
import type { AxiosResponse } from 'axios'
import { client } from '@/services/client'
import { useSpace } from '@/store/modules/App/space'
import type {
  Component,
  ComponentGroup,
  Story,
  ComponentVersion,
  Preset,
} from '@/types'
import type { BlockSchema, ComponentEndpoint } from '@/types/components'
import { BLOCK_LIBRARY_MENU_ITEMS } from '@/components/ContentManager/BlockLibraryPage/constants'

export type State = {
  components: Component[]
  component_groups: ComponentGroup[]
  grouped_components: Component[]
}

const state = (): State => ({
  components: [],
  component_groups: [],
  grouped_components: [],
})

export const useComponents = defineStore('component', {
  state,
  getters: {
    getRootComponents: (state: State): Component[] => {
      const mappedUuids = new Set(
        state.component_groups.map((group) => group.uuid),
      )
      return (
        state.components.filter(
          (component) => !mappedUuids.has(component.component_group_uuid || ''),
        ) || []
      )
    },
    getComponents: (state: State): Component[] => state.components || [],
    getGroupedComponents: (state: State): Component[] =>
      state.grouped_components || [],
    getComponentGroups: (state: State): ComponentGroup[] =>
      state.component_groups || [],
    getStoryComponents:
      (state: State) =>
      (story: Story | Partial<Story>): Component[] => {
        const storyString = JSON.stringify(story)
        return state.components.filter((c) => {
          return storyString.includes(c.name)
        })
      },
  },
  actions: {
    getComponent(name: string): Component | undefined {
      return this.components.find((component) => component.name === name)
    },

    setComponentsAndGroups({
      components,
      component_groups,
    }: ComponentEndpoint): void {
      this.components = components
      this.component_groups = component_groups
    },

    setGroupComponents(data: ComponentEndpoint): void {
      this.grouped_components = data.components
    },

    updateComponents(data: Component): void {
      this.components = [...this.components, data]
    },

    removeComponent(id: number): void {
      this.components = this.components.filter(
        (component) => component.id !== id,
      )
    },

    updateGroupNameInList({
      newGroupName,
      groupId,
    }: {
      groupId: number
      newGroupName?: string
    }): void {
      const updatedGroup = this.component_groups.find(
        (folder) => folder.id === groupId,
      )
      if (updatedGroup && newGroupName) {
        updatedGroup.name = newGroupName
      }
    },

    async loadComponents(payload: {
      sortBy?: string
      groupUuid?: string
      spaceId: number | string
    }): Promise<void> {
      try {
        const spaceStore = useSpace()

        const { sortBy, groupUuid, spaceId } = payload
        const activeSpaceId = spaceId ?? spaceStore.space.id
        const isRoot = groupUuid === BLOCK_LIBRARY_MENU_ITEMS.root.value

        const params = []
        if (sortBy) {
          params.push(`sort_by=${sortBy}`)
        }
        if (groupUuid && !isRoot) {
          params.push(`in_group=${groupUuid}`)
        }

        const queryString = params.length ? `?${params.join('&')}` : ''
        const promise = client.get(
          `spaces/${activeSpaceId}/components${queryString}`,
          {
            region: spaceStore.region,
          },
        )

        const { data } = await promise

        if (groupUuid && !isRoot) {
          return this.setGroupComponents(data)
        }

        this.setComponentsAndGroups({
          components: data.components,
          component_groups: data.component_groups,
        })

        delete data.components
        delete data.component_groups
      } catch (err) {
        return Promise.reject(err)
      }
    },

    async deleteGroup({
      spaceId,
      groupId,
    }: {
      spaceId: number | string
      groupId: number
    }): Promise<void> {
      return client.delete(`spaces/${spaceId}/component_groups/${groupId}`, {
        region: useSpace().region,
      })
    },

    async createComponentGroup({
      spaceId,
      groupName,
      parentId = null,
    }: {
      spaceId: number | string
      groupName: string
      parentId?: number | null
    }): Promise<AxiosResponse | void> {
      await client.post(
        `spaces/${spaceId}/component_groups`,
        {
          component_group: {
            name: groupName,
            parent_id: parentId,
          },
        },
        {
          region: useSpace().region,
        },
      )
      return this.loadComponents({ spaceId })
    },

    async updateComponentGroup({
      payload,
      spaceId,
      groupId,
    }: {
      payload: { name?: string; parent_id?: number }
      spaceId: number
      groupId: number
    }): Promise<void> {
      await client.put(
        `spaces/${spaceId}/component_groups/${groupId}`,
        {
          component_group: { ...payload },
        },
        {
          region: useSpace().region,
        },
      )

      this.updateGroupNameInList({ newGroupName: payload.name, groupId })

      return this.loadComponents({ spaceId })
    },

    async createComponents(payload: {
      component: Partial<Component>
      spaceId: string | number
    }): Promise<Component> {
      const { component, spaceId } = payload

      const newComponent = {
        ...component,
        component_group_uuid:
          component.component_group_uuid === BLOCK_LIBRARY_MENU_ITEMS.root.value
            ? null
            : component.component_group_uuid,
      }

      try {
        const { data } = await client.post(
          `spaces/${spaceId}/components`,
          newComponent,
          {
            region: useSpace().region,
          },
        )

        this.updateComponents(data.component)
        return Promise.resolve(data.component)
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async saveComponent(payload: {
      spaceId: number | string
      componentId: number
      data: Partial<Component>
    }): Promise<void> {
      const { spaceId, componentId, data } = payload

      try {
        await client.put(
          `spaces/${spaceId}/components/${componentId}`,
          {
            component: { ...data },
            update_content: true,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async moveComponents({
      spaceId,
      groupId,
      ids = [],
    }: {
      spaceId: number | string
      groupId: number
      ids: number[]
    }): Promise<void> {
      try {
        await client.post(
          `spaces/${spaceId}/components/bulk_update`,
          {
            component: {
              component_group_id: groupId,
            },
            ids,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async renameAttribute(payload: {
      spaceId: string | number
      componentId: string | number
      newName: string
      oldName: string
      storyId?: string | number
    }): Promise<void> {
      const { spaceId, storyId, componentId, newName, oldName } = payload

      await client.post(
        `spaces/${spaceId}/components/${componentId}/rename_attribute`,
        {
          new_name: newName,
          old_name: oldName,
          story_id: storyId,
        },
        {
          region: useSpace().region,
        },
      )

      return this.loadComponents({ spaceId })
    },

    async createPreset({
      spaceId,
      preset,
    }: {
      spaceId: number | string
      preset: Preset
    }): Promise<void> {
      try {
        await client.post(
          `spaces/${spaceId}/presets`,
          {
            preset,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadPreset({
      spaceId,
      presetId,
    }: {
      spaceId: number | string
      presetId: number
    }): Promise<Preset> {
      try {
        const { data } = await client.get(
          `spaces/${spaceId}/presets/${presetId}`,
          {
            region: useSpace().region,
          },
        )

        return data.preset.preset
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async editPreset({
      spaceId,
      preset,
      presetId,
    }: {
      spaceId: number
      preset: Preset
      presetId: number
    }): Promise<void> {
      try {
        await client.put(
          `spaces/${spaceId}/presets/${presetId}`,
          {
            preset,
          },
          {
            region: useSpace().region,
          },
        )

        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async deletePreset({
      spaceId,
      presetId,
    }: {
      spaceId: number
      presetId: string | number
    }): Promise<void> {
      if (!presetId) {
        return Promise.resolve()
      }

      try {
        await client.delete(`spaces/${spaceId}/presets/${presetId}`, {
          region: useSpace().region,
        })
        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadPresets({
      spaceId,
      componentId,
    }: {
      spaceId: number | string
      componentId: number
    }): Promise<Preset[]> {
      const { data } = await client.get(`spaces/${spaceId}/presets`, {
        params: {
          component_id: componentId,
        },
        region: useSpace().region,
      })

      return data.presets
    },

    async deleteComponent({
      componentId,
      spaceId,
    }: {
      componentId: number
      spaceId: number | string
    }): Promise<void> {
      await client.delete(`spaces/${spaceId}/components/${componentId}`, {
        region: useSpace().region,
      })
      return this.removeComponent(componentId)
    },

    async loadComponentVersions({
      spaceId,
      apiParameters,
    }: {
      spaceId: number | string
      apiParameters: {
        [key: string]: string | number | boolean
      }
    }): Promise<AxiosResponse<{ versions: ComponentVersion[] }>> {
      return await client.get(`spaces/${spaceId}/versions`, {
        params: apiParameters,
        region: useSpace().region,
      })
    },

    async previewComponentVersion({
      spaceId,
      componentId,
      version,
    }: {
      spaceId: number | string
      componentId: number
      version: ComponentVersion
    }): Promise<Component> {
      const { data } = await client.get(
        `spaces/${spaceId}/components/${componentId}/component_versions/${version.id}`,
        {
          region: useSpace().region,
        },
      )
      return data.component_version
    },

    async restoreComponentVersion({
      spaceId,
      componentId,
      versionId,
    }: {
      spaceId: number | string
      componentId: number
      versionId: number
    }): Promise<void> {
      try {
        await client.put(
          `spaces/${spaceId}/versions/${versionId}`,
          {
            model: 'components',
            model_id: componentId,
          },
          {
            region: useSpace().region,
          },
        )
        return this.loadComponents({ spaceId })
      } catch (e) {
        return Promise.reject(e)
      }
    },

    async loadLatestFieldVersion({
      spaceId,
      componentId,
      keys,
    }: {
      spaceId: number | string
      componentId: number
      keys?: string[]
    }): Promise<{
      schema: BlockSchema
    }> {
      const { data } = await client.get(
        `spaces/${spaceId}/field_versions/${componentId}`,
        {
          params: {
            fields: keys || [],
          },
          region: useSpace().region,
        },
      )

      return data.field_version
    },
  },
})
